import { useMemo } from "react";
import { FUNNEL_TYPES } from "../utils/contants";
import { FunnelContextArea } from "../types";
import { Release } from "../../../types";
import { useAccountData } from "../../../hooks/useAccountData";
import { AuthType } from "../../../hooks/useAuthMutations";
import {
  findFlowConfig,
  mapContextArea,
  getReleaseType,
} from "../config/flowConfigs";

/**
 * Manages visual flow indicators and contextual messaging for funnel flows.
 * Determines which indicators to show and what text to display based on user journey.
 */
interface UseFlowIndicatorsProps {
  authMethod?: AuthType;
  didPurchaseSubscription?: boolean;
  funnelType?: FUNNEL_TYPES;
  modalContext?: FunnelContextArea;
  release?: Release;
}

export function useFlowIndicators({
  authMethod,
  didPurchaseSubscription,
  funnelType,
  modalContext = FunnelContextArea.default,
  release,
}: UseFlowIndicatorsProps) {
  const accountData = useAccountData();

  // Determine if flow indicators should be shown
  const shouldShowSteps = useMemo(() => {
    // Check if feature flag is enabled
    return !!accountData?.features["alc_steps"];
  }, [accountData?.features, modalContext]);

  // Resolve flow configuration
  const flowData = useMemo(() => {
    if (!shouldShowSteps || !funnelType) {
      return null;
    }

    const context = mapContextArea(modalContext);

    if (!context) {
      return null;
    }

    const releaseType = getReleaseType(release);

    // Determine user state based on initial login status
    const isAuthFunnel = [
      FUNNEL_TYPES.login,
      FUNNEL_TYPES.signup,
      FUNNEL_TYPES.signupEmailPassword,
    ].includes(funnelType);
    const userState =
      authMethod || isAuthFunnel ? "startedLoggedOut" : "startedLoggedIn";
    const mappedFunnelType = isAuthFunnel
      ? FUNNEL_TYPES.authentication
      : funnelType;
    const mappedAuthMethod = isAuthFunnel
      ? funnelType === FUNNEL_TYPES.login
        ? AuthType.login
        : AuthType.signup
      : authMethod;

    const config = findFlowConfig(
      mappedFunnelType,
      context,
      releaseType,
      userState,
      mappedAuthMethod,
      didPurchaseSubscription,
      !!release?.release_user_purchase,
    );

    if (!config) {
      return null;
    }

    // Determine step count dynamically based on the flow
    // For startedLoggedIn flows, it's typically 1 step unless multi-step flow is configured
    // For startedLoggedOut flows, auth is step 1, other actions are step 2+
    const stepCount =
      userState === "startedLoggedIn"
        ? Math.max(config.stepNumber, 1) // At least 1 step for logged-in users
        : Math.max(config.stepNumber, 2); // At least 2 steps for logged-out users (auth + action)

    return {
      stepCount,
      currentStep: config.stepNumber,
      stepText: config.stepText,
      text: config.text,
      showStepIndicators: config.showIndicators,
    };
  }, [
    shouldShowSteps,
    funnelType,
    modalContext,
    release,
    authMethod,
    didPurchaseSubscription,
  ]);

  // Extract flow data with defaults
  const {
    stepCount = 2,
    currentStep = 1,
    stepText = "",
    text = "",
    showStepIndicators = false,
  } = flowData || {};

  const hasContent = shouldShowSteps && (!!stepText || !!text);

  return {
    // Flow data
    flowData,
    stepCount,
    currentStep,
    stepText,
    text,

    // Flags
    shouldShowSteps,
    showStepIndicators,
    hasContent,
  };
}
